import { Injectable, inject } from '@angular/core';
import { LuzulaDbNaming } from '@luzula/luzula-lib';
import { APP_CONFIG } from './app.config';

@Injectable({ providedIn: 'root' })
export class DatabaseConfigService {
  private config = inject(APP_CONFIG);
  /** Zentrale dev-/prod-Namensbildung (Lib). Löst die früher hier duplizierte Prefix-Logik ab. */
  private naming = inject(LuzulaDbNaming);

  /** DB-Namensraum dieser Bau-Variante (`boden`, `bpweb-mv`, `bpweb`), ohne dev-Prefix. */
  get namensraum(): string {
    return this.config.appName;
  }

  get punkteDbName(): string {
    return this.naming.name(`${this.config.appName}-punkte`);
  }

  get projekteDbName(): string {
    return this.naming.name(`${this.config.appName}-projekte`);
  }

  /**
   * Dedizierte DB für Zeichnungen (ein Doc je Feature). Eigene DB statt Array im
   * Projekt-Doc — verhindert Doc-Bloat/Resurrection und macht Löschen zu einem
   * nativen `_deleted`-Tombstone. dev-Präfix wie projekte/punkte (Projekt-Nutzerdaten).
   */
  get zeichnungenDbName(): string {
    return this.naming.name(`${this.config.appName}-zeichnungen`);
  }

  /**
   * Dedizierte DB für Bereiche (ein Doc je Bereich mit stabiler bereichId).
   * Eigene DB statt regions/projectPoints im Projekt-Doc — Name/Filter kleben
   * am Bereich (kein Index-Remap), Löschen ist ein nativer `_deleted`-Tombstone
   * (kein Resurrection-Bug). Siehe luzula-lib PLAN-bereiche-db.md.
   */
  get bereicheDbName(): string {
    return this.naming.name(`${this.config.appName}-bereiche`);
  }

  /**
   * Dedizierte DB für Druck-Layouts (ein Doc je Layout, selbst-enthalten).
   * Eigene DB statt Array im Projekt-Doc — wie zeichnungen/bereiche: Löschen ist
   * ein nativer `_deleted`-Tombstone, kein Doc-Bloat, projekt-scoped Sync.
   * dev-Präfix wie projekte/punkte (Projekt-Nutzerdaten).
   */
  get layoutsDbName(): string {
    return this.naming.name(`${this.config.appName}-layouts`);
  }

  /**
   * Dedizierte DB für editierbare Flächen (Standort/Humusform, ein Doc je
   * Fläche mit stabiler geomId) — topologisch korrektes Digitalisieren.
   * Eigene DB wie zeichnungen/bereiche: Löschen ist ein nativer `_deleted`-
   * Tombstone, kein Doc-Bloat, projekt-scoped Sync. Siehe PLAN-flaechen-linien.md.
   */
  get geomDbName(): string {
    return this.naming.name(`${this.config.appName}-geom`);
  }

  /**
   * Name der zentralen Listen-DB ("Single Source of Truth" für alle App-Listen).
   * Bewusst OHNE dev-Präfix: es gibt genau EINE `bpweb-listen` (Entscheidung
   * 2026-06-06) — hier NICHT `naming.name(...)`. Falls Dev doch isoliert werden
   * soll, hier `this.naming.name(...)` nutzen wie bei punkte/projekte — das ist
   * der EINZIGE Ort, der den Namen vergibt.
   */
  get listenDbName(): string {
    return `${this.config.appName}-listen`;
  }

  get isDevelopment(): boolean {
    return this.naming.isDevelopment;
  }
}
